import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Autocomplete,
  CircularProgress,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const API_URL = import.meta.env.VITE_API_URL;

type Region = {
  regionId: number;
  regionName: string;
};

type City = {
  citiesId: number;
  name: string;
  image: string | null;
  priority: boolean;
  regionId: number | null;
  Region: Region | null;
};

type RegionAutocompleteProps = {
  value: Region | null;
  onChange: (region: Region | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  sx?: object;
};

const ADD_NEW_SENTINEL = '__ADD_NEW__';

function RegionAutocomplete({
  value,
  onChange,
  label = 'Область',
  size = 'medium',
  fullWidth = false,
  sx,
}: RegionAutocompleteProps) {
  const [options, setOptions] = useState<Region[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchRegions = useCallback(async (search: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/region?search=${encodeURIComponent(search)}`);
      const data = await res.json();
      setOptions(data.data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchRegions(inputValue), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, fetchRegions]);

  const createRegion = async (name: string): Promise<Region | null> => {
    const res = await fetch(`${API_URL}/region`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ regionName: name }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.data ?? data;
  };

  const exactMatch = options.some(
    (o) => o.regionName.toLowerCase() === inputValue.toLowerCase(),
  );
  const showAddNew = inputValue.trim().length > 0 && !exactMatch;

  type OptionType = Region | typeof ADD_NEW_SENTINEL;

  const allOptions: OptionType[] = showAddNew
    ? [...options, ADD_NEW_SENTINEL]
    : options;

  return (
    <Autocomplete<OptionType, false, false, false>
      sx={sx}
      fullWidth={fullWidth}
      size={size}
      options={allOptions}
      value={value}
      inputValue={inputValue}
      loading={loading}
      filterOptions={(x) => x} 
      getOptionLabel={(option) => {
        if (option === ADD_NEW_SENTINEL) return `Додати: "${inputValue}"`;
        return (option as Region).regionName;
      }}
      isOptionEqualToValue={(option, val) => {
        if (option === ADD_NEW_SENTINEL || val === ADD_NEW_SENTINEL) return false;
        return (option as Region).regionId === (val as Region).regionId;
      }}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      onChange={async (_, selected) => {
        if (!selected) {
          onChange(null);
          return;
        }
        if (selected === ADD_NEW_SENTINEL) {
          const created = await createRegion(inputValue.trim());
          if (created) {
            setOptions((prev) => [created, ...prev]);
            onChange(created);
          }
          return;
        }
        onChange(selected as Region);
      }}
      renderOption={(props, option) => {
        if (option === ADD_NEW_SENTINEL) {
          return (
            <li {...props} key="add-new">
              <Typography color="primary" fontWeight={600}>
                + Додати: «{inputValue}»
              </Typography>
            </li>
          );
        }
        const region = option as Region;
        return (
          <li {...props} key={region.regionId}>
            {region.regionName}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default function Cities(): JSX.Element {
  const [input, setInput] = useState<string>('');
  const [priority, setPriority] = useState<boolean>(false);
  const [newRegion, setNewRegion] = useState<Region | null>(null);

  const [cities, setCities] = useState<City[]>([]);
  const [total, setTotal] = useState<number>(0);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [sortField, setSortField] = useState<'name' | 'priority'>('name');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const [filterRegion, setFilterRegion] = useState<Region | null>(null);

  const [snackbar, setSnackbar] = useState<{
    text: string;
    color: 'success' | 'error';
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const isMultiple = input.includes(',');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchCities = useCallback(async (): Promise<void> => {
    const params = new URLSearchParams({
      page: String(page + 1),
      limit: String(rowsPerPage),
      field: sortField,
      order: sortOrder,
      search: debouncedSearch,
    });
    if (filterRegion) params.set('regionId', String(filterRegion.regionId));

    const res = await fetch(`${API_URL}/city?${params.toString()}`);
    const data = await res.json();
    setCities(data.data);
    setTotal(data.total);
  }, [page, rowsPerPage, sortField, sortOrder, debouncedSearch, filterRegion]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const handleCreate = async (): Promise<void> => {
    if (!isMultiple) {
      const res = await fetch(`${API_URL}/cities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: input.trim(),
          priority,
          ...(newRegion ? { regionId: newRegion.regionId } : {}),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setSnackbar({ text: data.error, color: 'error' });
        return;
      }

      setCities((prev) => [data.data, ...prev]);
      setInput('');
      setNewRegion(null);
      return;
    }

    const names = input.split(',').map((n) => n.trim());

    const res = await fetch(`${API_URL}/cities/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        names,
        priority,
        ...(newRegion ? { regionId: newRegion.regionId } : {}),
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      setSnackbar({ text: 'Помилка', color: 'error' });
      return;
    }

    if (data.skipped.length === 0) {
      setSnackbar({ text: 'Всі міста додані успішно', color: 'success' });
    } else {
      setSnackbar({
        text: `Додані всі міста крім — ${data.skipped.join(', ')}. Вони вже є в адмін панелі`,
        color: 'error',
      });
    }

    fetchCities();
    setInput('');
    setNewRegion(null);
  };

  const handleUpdate = async (
    citiesId: number,
    field: 'name' | 'priority' | 'regionId',
    value: string | boolean | number | null,
  ): Promise<void> => {
    const prev = [...cities];

    setCities((list) =>
      list.map((c) => (c.citiesId === citiesId ? { ...c, [field]: value } : c)),
    );

    const res = await fetch(`${API_URL}/cities/${citiesId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    });

    if (!res.ok) {
      setCities(prev);
      setSnackbar({ text: 'Помилка оновлення', color: 'error' });
      return;
    }

    if (field === 'regionId') {
      fetchCities();
    }
  };

  const handleRegionUpdate = async (citiesId: number, region: Region | null) => {
    const prev = [...cities];

    setCities((list) =>
      list.map((c) =>
        c.citiesId === citiesId
          ? { ...c, regionId: region?.regionId ?? null, Region: region }
          : c,
      ),
    );

    const res = await fetch(`${API_URL}/cities/${citiesId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ regionId: region?.regionId ?? null }),
    });

    if (!res.ok) {
      setCities(prev);
      setSnackbar({ text: 'Помилка оновлення', color: 'error' });
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (deleteId === null) return;

    const prev = [...cities];
    setCities((list) => list.filter((c) => c.citiesId !== deleteId));
    setDeleteId(null);

    const res = await fetch(`${API_URL}/cities/${deleteId}`, { method: 'DELETE' });

    if (!res.ok) {
      setCities(prev);
      setSnackbar({ text: 'Помилка видалення', color: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="center" mb={4}>
        <Paper sx={{ p: 3, width: 500 }}>
          <Typography mb={1}>Назва міста</Typography>

          <TextField
            fullWidth
            multiline
            minRows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />

          <Typography variant="caption" color="text.secondary">
            Ви можете додати декілька міст одразу вказавши їх через кому.
            Наприклад: Київ, Львів
          </Typography>

          <Box mt={2}>
            <RegionAutocomplete
              fullWidth
              value={newRegion}
              onChange={setNewRegion}
              label="Область"
            />
          </Box>

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={priority}
                onChange={(e) => setPriority(e.target.checked)}
              />
            }
            label={
              isMultiple
                ? 'Показувати користувачу в боті всі міста пріорітетно'
                : 'Показувати користувачу в боті це місто пріорітетно'
            }
          />

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handleCreate}
          >
            {isMultiple ? 'Додати міста' : 'Додати місто'}
          </Button>
        </Paper>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          sx={{ flex: '1 1 200px' }}
          placeholder="Пошук міст..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />

        <Box sx={{ flex: '1 1 220px' }}>
          <RegionAutocomplete
            fullWidth
            value={filterRegion}
            onChange={(r) => {
              setFilterRegion(r);
              setPage(0);
            }}
            label="Фільтр по області"
          />
        </Box>

        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Сортування</InputLabel>
          <Select
            label="Сортування"
            value={`${sortField}_${sortOrder}`}
            onChange={(e) => {
              const [field, order] = String(e.target.value).split('_');
              setSortField(field as 'name' | 'priority');
              setSortOrder(order as 'ASC' | 'DESC');
              setPage(0);
            }}
          >
            <MenuItem value="name_ASC">Назва (А–Я)</MenuItem>
            <MenuItem value="name_DESC">Назва (Я–А)</MenuItem>
            <MenuItem value="priority_ASC">Пріоритетні</MenuItem>
            <MenuItem value="priority_DESC">Не пріоритетні</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Назва</TableCell>
            <TableCell sx={{ minWidth: 220 }}>Область</TableCell>
            <TableCell>Пріоритет</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {cities.map((city) => (
            <TableRow key={city.citiesId}>
              <TableCell>
                <TextField
                  value={city.name}
                  onChange={(e) =>
                    handleUpdate(city.citiesId, 'name', e.target.value)
                  }
                  size="small"
                />
              </TableCell>

              <TableCell sx={{ minWidth: 220 }}>
                <RegionAutocomplete
                  size="small"
                  fullWidth
                  value={city.Region ?? null}
                  onChange={(region) => handleRegionUpdate(city.citiesId, region)}
                  label=""
                />
              </TableCell>

              <TableCell>
                <Checkbox
                  checked={city.priority}
                  onChange={(e) =>
                    handleUpdate(city.citiesId, 'priority', e.target.checked)
                  }
                />
              </TableCell>

              <TableCell>
                <IconButton onClick={() => setDeleteId(city.citiesId)}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage: number) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar?.text}
        ContentProps={{
          sx: {
            backgroundColor: snackbar?.color === 'success' ? 'green' : 'red',
          },
        }}
      />

      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Видалити місто?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Скасувати</Button>
          <Button color="error" onClick={confirmDelete}>
            Видалити
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}